import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [siteId, setSiteId] = useState(localStorage.getItem('adminSiteId') || 'default');
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('adminToken'));
  const [authNotice, setAuthNotice] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authToken = params.get('authToken');
    const authProvider = params.get('authProvider');
    const authError = params.get('authError');

    if (authToken) {
      localStorage.setItem('adminToken', authToken);
      setToken(authToken);
      if (authProvider === 'discord') {
        setAuthNotice('Signed in with Discord successfully.');
      }
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (authError) {
      setAuthNotice(decodeURIComponent(authError));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch('/api/auth/verify', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);

        // Ensure siteId remains available on the client. The verify endpoint
        // may return `user` without a `siteId` field, so try multiple fallbacks:
        // 1) `data.user.siteId` if present
        // 2) decode siteId from returned token payload
        // 3) previously-saved `adminSiteId` in localStorage
        let resolvedSite = null;
        if (data.user && data.user.siteId) resolvedSite = data.user.siteId;
        if (!resolvedSite && data.token) {
          try {
            const parts = String(data.token).split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              if (payload && payload.siteId) resolvedSite = payload.siteId;
            }
          } catch (e) {
            // ignore decode errors
          }
        }
        if (!resolvedSite) {
          const stored = localStorage.getItem('adminSiteId');
          if (stored) resolvedSite = stored;
        }

        if (resolvedSite) {
          setSiteId(resolvedSite);
          localStorage.setItem('adminSiteId', resolvedSite);
        }
        if (data.token && data.token !== token) {
          localStorage.setItem('adminToken', data.token);
          setToken(data.token);
        }
      } else {
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const apiFetch = async (path, options = {}) => {
    const opts = { ...(options || {}) };
    opts.headers = opts.headers || {};
    if (token) {
      opts.headers.Authorization = `Bearer ${token}`;
    }

    const method = (opts.method || 'GET').toUpperCase();

    // For GET requests, append siteId as query param when not present
    if (method === 'GET') {
      try {
        const url = new URL(path, window.location.origin);
        if (siteId && !url.searchParams.has('siteId')) {
          url.searchParams.set('siteId', siteId);
        }
        return fetch(url.toString(), opts);
      } catch (e) {
        // Fallback for non-URL paths
        const sep = path.includes('?') ? '&' : '?';
        const suffix = siteId ? `${sep}siteId=${encodeURIComponent(siteId)}` : '';
        return fetch(path + suffix, opts);
      }
    }

    // For non-GET with JSON body, ensure siteId is included
    if (opts.body && typeof opts.body === 'string' && opts.headers['Content-Type'] && opts.headers['Content-Type'].includes('application/json')) {
      try {
        const parsed = JSON.parse(opts.body);
        if (siteId && !parsed.siteId) {
          parsed.siteId = siteId;
          opts.body = JSON.stringify(parsed);
        }
      } catch (e) {
        // ignore parse errors
      }
    } else if (!opts.body && siteId && opts.headers['Content-Type'] && opts.headers['Content-Type'].includes('application/json')) {
      opts.body = JSON.stringify({ siteId });
    }

    return fetch(path, opts);
  };

  const login = async (selectedSiteId, username, password) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ siteId: selectedSiteId, username, password })
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        const fallback = response.statusText || 'Unexpected server response';
        throw new Error(`Login failed: ${fallback}`);
      }

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // If MFA is required, the server returns a temporary token and a flag
      if (data && data.mfaRequired) {
        return { success: true, mfaRequired: true, tempToken: data.tempToken };
      }

      localStorage.setItem('adminToken', data.token);
      localStorage.setItem('adminSiteId', selectedSiteId);
      setToken(data.token);
      setSiteId(selectedSiteId);
      setUser(data.user);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const verifyMfa = async (tempToken, code) => {
    try {
      const response = await fetch('/api/auth/mfa/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tempToken, code })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'MFA verification failed');
      }

      localStorage.setItem('adminToken', data.token);
      const resolvedSite = (data.user && data.user.siteId) || localStorage.getItem('adminSiteId') || siteId;
      if (resolvedSite) {
        localStorage.setItem('adminSiteId', resolvedSite);
        setSiteId(resolvedSite);
      }
      setToken(data.token);
      setUser(data.user);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const registerPasskeyRequest = async () => {
    try {
      const response = await fetch('/api/users/webauthn/register-request', { headers: { Authorization: `Bearer ${token}` } });
      return await response.json();
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const registerPasskeyResponse = async (attestation) => {
    try {
      const response = await fetch('/api/users/webauthn/register-response', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(attestation)
      });
      return await response.json();
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const passkeyLoginRequest = async (username, selectedSiteId) => {
    try {
      const response = await fetch('/api/auth/passkey/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, siteId: selectedSiteId })
      });
      return await response.json();
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const passkeyLoginVerify = async (username, selectedSiteId, assertion) => {
    try {
      const response = await fetch('/api/auth/passkey/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, siteId: selectedSiteId, assertion })
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || 'Passkey login failed' };
      localStorage.setItem('adminToken', data.token);
      localStorage.setItem('adminSiteId', selectedSiteId);
      setToken(data.token);
      setSiteId(selectedSiteId);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const listPasskeys = async () => {
    try {
      const response = await fetch('/api/users/webauthn/credentials', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || 'Failed to list passkeys', credentials: [] };
      return { success: true, credentials: data.credentials || [] };
    } catch (err) {
      return { success: false, error: err.message, credentials: [] };
    }
  };

  const removePasskey = async (credentialId) => {
    try {
      const response = await fetch(`/api/users/webauthn/credentials/${encodeURIComponent(credentialId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || 'Failed to remove passkey' };
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const disableMfa = async (code) => {
    try {
      const response = await fetch('/api/users/mfa/disable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code })
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || 'Failed to disable MFA' };
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const getProfile = async () => {
    try {
      const response = await fetch('/api/users/me', { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) return null;
      const data = await response.json();
      return data.user || null;
    } catch (err) {
      return null;
    }
  };

  const updateProfile = async (updates) => {
    try {
      const response = await fetch('/api/users/me', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates)
      });
      const data = await response.json();
      if (!response.ok) return { success: false, error: data.error || 'Update failed' };
      setUser(data.user);
      return { success: true, user: data.user };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const loginWithDiscord = async (selectedSiteId) => {
    try {
      if (!selectedSiteId || selectedSiteId === 'default') {
        throw new Error('Please choose a server before using Discord login.');
      }

      const response = await fetch(`/api/auth/discord/url?siteId=${encodeURIComponent(selectedSiteId)}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to start Discord login');
      }

      window.location.href = data.url;
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      if (token) {
        // Try to notify server to revoke the session; ignore failures
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (err) {
      // ignore
    } finally {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminSiteId');
      setToken(null);
      setSiteId('default');
      setUser(null);
    }
  };

  const normalizePermissions = (permissions) => {
    if (!permissions) return [];
    if (Array.isArray(permissions)) return permissions;
    if (typeof permissions === 'object') {
      return Object.keys(permissions).filter(key => permissions[key] === true);
    }
    if (typeof permissions === 'string') {
      try {
        const parsed = JSON.parse(permissions);
        return normalizePermissions(parsed);
      } catch (error) {
        return permissions
          .split(',')
          .map((permission) => permission.trim())
          .filter(Boolean);
      }
    }
    return [];
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'root') return true;

    const permissions = normalizePermissions(user.permissions);
    if (permissions.includes('*') || permissions.includes(permission)) {
      return true;
    }

    const segments = permission.split('.');
    for (let index = segments.length - 1; index > 0; index -= 1) {
      const wildcard = `${segments.slice(0, index).join('.')}.*`;
      if (permissions.includes(wildcard)) {
        return true;
      }
    }

    return false;
  };

  const hasRole = (role) => {
    if (!user) return false;
    if (user.role === 'root') return true;
    return user.role === role;
  };

  const value = {
    user,
    siteId,
    setSiteId,
    token,
    loading,
    apiFetch,
    login,
    loginWithDiscord,
    verifyMfa,
    registerPasskeyRequest,
    registerPasskeyResponse,
    passkeyLoginRequest,
    passkeyLoginVerify,
    listPasskeys,
    removePasskey,
    disableMfa,
    getProfile,
    updateProfile,
    logout,
    hasPermission,
    hasRole,
    authNotice,
    clearAuthNotice: () => setAuthNotice(''),
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
